import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  startWith,
  Subscription,
  tap,
} from 'rxjs';
import { CustomDropDownComponent } from '../custom-drop-down/custom-drop-down.component';
import { IEnvironment } from '../../../models/environment.model';
import { FormStateService } from '../../../services/formly/form-state.service';
import { TranslateService } from '@ngx-translate/core';
import {
  EGenericInputTriggerType,
  GenericInputStatusEnum,
} from '../../../models/irembo-generic-input-status.enum';
import {
  checkForValidFields,
  findFormControl,
  getPopulateReferenceForm,
  handleErrorMapping,
  resetFieldsToPopulate,
  subscribeToResetFieldFetchData,
  updateUrlWithApiGatewayBaseUrl,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { v4 as uuidv4 } from 'uuid';

/**
 * Custom Dropdown component with data fetching and pagination capabilities.
 * Extends CustomDropDownComponent to provide integration with external APIs,
 * field listeners, pagination support, and search functionality.
 */
@Component({
  selector: 'irembogov-custom-dropdown-data-fetch-paginated',
  templateUrl: './custom-dropdown-data-fetch-paginated.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDropdownDataFetchPaginatedComponent
  extends CustomDropDownComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  statusClass: string | undefined;
  fieldsToPopulate: Record<string, string>[] = [];
  private fieldListenerSubscriptions = new Subscription();
  private lastPayload: string | null = null;
  private allItems: Record<string, unknown>[] = [];
  private isSearchActive = false;
  // Analytics: emit exactly ONE lookup outcome per initial load (not per
  // infinite-scroll page or per search keystroke). Reset only in fetchInitialData.
  private hasEmittedInitialOutcome = false;

  constructor(
    http: HttpClient,
    cd: ChangeDetectorRef,
    formStateService: FormStateService,
    translateService: TranslateService,
    @Inject('environment') environment: IEnvironment
  ) {
    super(http, cd, formStateService, translateService, environment);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    checkForValidFields(this.field);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // Add field listener subscription for data fetch functionality
    subscribeToResetFieldFetchData(
      this.field,
      (this as any).formStateService,
      this.fieldsToPopulate,
      getPopulateReferenceForm(this.field, this.form)
    );

    if (
      this.field.props['triggerType'] ===
      EGenericInputTriggerType.FIELD_LISTENER
    ) {
      this.setupFieldListeners();
    }

    if (this.fieldsToPopulate?.length > 0) {
      this.addToSubscriptions(
        this.field.formControl.valueChanges
          .pipe(
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) === JSON.stringify(b)
            )
          )
          .subscribe({
            next: () => {
              if (this.field.formControl.invalid) {
                const formRef = getPopulateReferenceForm(this.field, this.form);
                resetFieldsToPopulate(
                  this.fieldsToPopulate,
                  formRef,
                  (this as any).formStateService
                );
              }
            },
          })
      );
    }
  }

  // Load more data for pagination
  getMoreDataMethod(event: boolean) {
    if (!event || this.loading || this.currentPage >= this.totalPages) {
      return;
    }

    if (!this.field.props['url'] || !this.field.props['endpointCode']) {
      console.error(
        'Missing required properties for pagination: url and endpointCode'
      );
      return;
    }

    const { dataset } = this.field.props;
    if (!dataset) {
      console.error('Missing dataset configuration for pagination');
      return;
    }

    const httpMethod = dataset['httpMethod'] || 'POST';
    const url = this.field.props['useBaseUrl']
      ? updateUrlWithApiGatewayBaseUrl(
          this.field.props['url'],
          this.getEnvironment()
        )
      : this.field.props['url'];
    const params = { ...(dataset['params'] || {}) };
    const headers = dataset['headers'] || {};
    const dataField = dataset['dataField'] || '';

    // Increment page for next batch
    this.currentPage++;

    // Set pagination parameters
    const pageKey = dataset['pageKey'] || 'page';
    const sizeKey = dataset['sizeKey'] || 'size';
    params[pageKey] = this.currentPage;
    if (!params[sizeKey]) {
      params[sizeKey] = 20; // Default page size
    }

    // Add payload mappings to params
    const payload = this.buildCurrentPayload();
    Object.assign(params, payload);

    // Set loading state and clear previous errors
    this.prepareRequestState();

    if (httpMethod === 'POST') {
      this.getDataByIntegrationPostMethod(url, dataField, params, headers);
    } else {
      this.getDataByIntegrationGetMethod(url, dataField, params, headers);
    }
  }

  // Search functionality
  searchData(value: any) {
    const { dataset } = this.field.props;

    // Handle empty search - show all items
    if (!value || (typeof value === 'string' && !value.trim())) {
      this.isSearchActive = false;
      if (dataset?.['searchLocal']) {
        // Reset to show all locally cached items
        this.items = [...this.allItems];
        (this as any).cd.detectChanges();
      } else {
        // Reset API search to show all data
        this.fetchInitialData();
      }
      return;
    }

    // Mark search as active
    this.isSearchActive = true;

    // Use local search if specified, otherwise use API search
    if (dataset?.['searchLocal']) {
      this.performLocalSearch(value);
    } else {
      this.performApiSearch(value);
    }
  }

  // Perform API-based search
  private performApiSearch(value: string) {
    const { dataset } = this.field.props;
    const httpMethod = dataset['httpMethod'] || 'POST';
    const url = this.field.props['useBaseUrl']
      ? updateUrlWithApiGatewayBaseUrl(
          this.field.props['url'],
          this.getEnvironment()
        )
      : this.field.props['url'];
    const params = { ...(dataset['params'] || {}) };
    const headers = dataset['headers'] || {};
    const dataField = dataset['dataField'] || '';

    // Reset pagination for new search
    this.items = [];
    const pageStart = dataset['pageStart'] || 0;
    this.currentPage = pageStart;
    this.totalPages = 0;

    // Set pagination parameters
    const pageKey = dataset['pageKey'] || 'page';
    const sizeKey = dataset['sizeKey'] || 'size';
    params[pageKey] = pageStart;
    if (!params[sizeKey]) {
      params[sizeKey] = 20; // Default page size
    }

    // Add search parameter
    const searchKey = dataset['searchKey'] || 'search';
    params[searchKey] = value.trim();

    // Add payload mappings to params
    const payload = this.buildCurrentPayload();
    Object.assign(params, payload);

    // Set loading state and clear previous errors
    this.prepareRequestState();

    if (httpMethod === 'POST') {
      this.getDataByIntegrationPostMethod(url, dataField, params, headers);
    } else {
      this.getDataByIntegrationGetMethod(url, dataField, params, headers);
    }
  }

  // Perform local search through cached items
  private performLocalSearch(searchTerm: string) {
    if (!this.allItems.length) return;

    const term = searchTerm.toLowerCase().trim();
    const bindLabel = this.field.props['bindLabel'] || 'label';

    this.items = this.allItems.filter(item => {
      // Search in bind label field
      const labelValue = item[bindLabel];
      if (
        labelValue &&
        typeof labelValue === 'string' &&
        labelValue.toLowerCase().includes(term)
      ) {
        return true;
      }

      // Search in all string fields as fallback
      return Object.values(item).some(
        value => typeof value === 'string' && value.toLowerCase().includes(term)
      );
    });

    (this as any).cd.detectChanges();
  }

  // Field listener setup from CustomGenericDataFetchComponent
  setupFieldListeners() {
    const { payloadKeyMappings, useMultiplePayloadKeys, payloadKey } =
      this.field.props;
    const debounceMs = this.field.props['debounceTime'] || 500;
    const formRef = getPopulateReferenceForm(this.field, this.form);

    // If we have static-only payload, trigger fetch immediately
    if (useMultiplePayloadKeys && this.hasOnlyStaticPayload()) {
      setTimeout(() => this.fetchInitialData(), 100);
      return;
    }

    // If single payload and no field dependency, trigger fetch
    if (!useMultiplePayloadKeys && !payloadKey) {
      setTimeout(() => this.fetchInitialData(), 100);
      return;
    }

    if (!useMultiplePayloadKeys) {
      const control = findFormControl(formRef, payloadKey);
      if (control) {
        this.fieldListenerSubscriptions.add(
          control.valueChanges
            .pipe(
              distinctUntilChanged(),
              debounceTime(debounceMs),
              tap(() => {
                this.clearGlobalErrorForField();
              })
            )
            .subscribe(value => {
              if (value !== null && value !== undefined) {
                this.fetchInitialData();
              }
            })
        );
        return;
      }
    }

    const fieldControls: AbstractControl[] = [];
    const fieldConfigs: any[] = [];

    payloadKeyMappings.forEach((mapping: any) => {
      if (!mapping.isStatic && mapping.sourceField) {
        const control = findFormControl(formRef, mapping.sourceField);
        if (control) {
          fieldControls.push(control);
          fieldConfigs.push(mapping);
        }
      }
    });

    if (!fieldControls.length) {
      // All mappings are static, trigger fetch immediately
      setTimeout(() => this.fetchInitialData(), 100);
      return;
    }

    const fieldObservables = fieldControls.map(control =>
      control.valueChanges.pipe(
        control.value != null && control.value !== ''
          ? startWith(control.value)
          : distinctUntilChanged(),
        debounceTime(debounceMs)
      )
    );

    this.fieldListenerSubscriptions.add(
      combineLatest(fieldObservables)
        .pipe(tap(() => this.clearGlobalErrorForField()))
        .subscribe(values => {
          if (values.every(v => v !== null && v !== undefined)) {
            this.fetchInitialData();
          }
        })
    );
  }

  // Build current payload from form values and static mappings
  buildCurrentPayload(): Record<string, unknown> {
    const { useMultiplePayloadKeys, payloadKey, payloadKeyMappings } =
      this.field.props;
    const formRef = getPopulateReferenceForm(this.field, this.form);

    if (!useMultiplePayloadKeys) {
      if (!payloadKey) return {};
      const control = findFormControl(formRef, payloadKey);
      return { [payloadKey]: control?.value };
    }

    const payload: Record<string, unknown> = {};
    if (Array.isArray(payloadKeyMappings)) {
      payloadKeyMappings.forEach((mapping: any) => {
        if (mapping.isStatic) {
          payload[mapping.key] = mapping.staticValue;
        } else if (mapping.sourceField) {
          const control = findFormControl(formRef, mapping.sourceField);
          payload[mapping.key] = control?.value;
        }
      });
    }

    return payload;
  }

  // Check if all payload mappings are static
  private hasOnlyStaticPayload(): boolean {
    const { payloadKeyMappings } = this.field.props;
    if (!Array.isArray(payloadKeyMappings)) return false;
    return payloadKeyMappings.every((mapping: any) => mapping.isStatic);
  }

  // Fetch initial data for dropdown
  fetchInitialData() {
    const { dataset } = this.field.props;
    if (
      !dataset ||
      !this.field.props['url'] ||
      !this.field.props['endpointCode']
    ) {
      return;
    }

    const httpMethod = dataset['httpMethod'] || 'POST';
    const url = this.field.props['useBaseUrl']
      ? updateUrlWithApiGatewayBaseUrl(
          this.field.props['url'],
          this.getEnvironment()
        )
      : this.field.props['url'];
    const params = { ...(dataset['params'] || {}) };
    const headers = dataset['headers'] || {};
    const dataField = dataset['dataField'] || '';

    // Add all payload mappings to params
    const payload = this.buildCurrentPayload();
    const currentPayload = JSON.stringify(payload);

    // Reset model if payload has changed
    if (this.lastPayload !== null && this.lastPayload !== currentPayload) {
      this.field.formControl.setValue(null);
    }
    this.lastPayload = currentPayload;

    // Reset pagination and search state
    this.currentPage = 0;
    this.items = [];
    this.isSearchActive = false;
    this.hasEmittedInitialOutcome = false;

    // Set initial pagination
    const pageKey = dataset['pageKey'] || 'page';
    const sizeKey = dataset['sizeKey'] || 'size';
    const pageStart = dataset['pageStart'] || 0;
    params[pageKey] = pageStart;
    if (!params[sizeKey]) {
      params[sizeKey] = 20; // Default page size
    }

    Object.assign(params, payload);

    this.prepareRequestState();

    if (httpMethod === 'POST') {
      this.getDataByIntegrationPostMethod(url, dataField, params, headers);
    } else {
      this.getDataByIntegrationGetMethod(url, dataField, params, headers);
    }
  }

  private getValueFromNestedObjectLocal(
    obj: Record<string, unknown>,
    keyString: string
  ): Record<string, unknown>[] {
    if (!keyString || !obj) {
      return [];
    }

    const keys = keyString.split('.');
    let value: any = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else if (typeof value === 'string') {
        // Try to parse as JSON if it's a string
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && key in parsed) {
            value = parsed[key];
          } else {
            throw new Error(`Key '${key}' not found in parsed JSON`);
          }
        } catch (parseError) {
          throw new Error(`Invalid key path '${keyString}': ${parseError}`);
        }
      } else {
        throw new Error(
          `Invalid key path '${keyString}': key '${key}' not found`
        );
      }
    }

    // Ensure we return an array
    if (Array.isArray(value)) {
      return value as Record<string, unknown>[];
    } else if (value !== null && value !== undefined) {
      return [value as Record<string, unknown>];
    } else {
      return [];
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.fieldListenerSubscriptions.unsubscribe();
  }

  // Reusable error handling method
  private handleIntegrationError(error: any): void {
    const { errors, statusClass } = handleErrorMapping(
      error?.error || error,
      this.field.props['errorMapping'],
      'invalidGenericInput',
      this.field,
      this.field.formControl,
      true
    );

    const errorKey = Object.keys(errors)[0];
    const errorMessage =
      errors[errorKey]?.message || this.field.validation?.messages?.[errorKey];

    if (this.field?.props?.['showGlobalError'] && errorMessage) {
      (this as any).formStateService.addGlobalError(this.field, errorMessage);
    }

    this.statusClass = statusClass;
    this.field.formControl.setErrors(errors);
    this.loading = false;
    this.deleteFetchedOptionsDataKeyInFormState();
    (this as any).cd.detectChanges();
    this.emitInitialOutcomeOnce('failure', error);
  }

  // Reusable success handling method
  private handleIntegrationSuccess(
    res: Record<string, unknown>,
    dataField: string
  ): void {
    this.statusClass = GenericInputStatusEnum.SUCCESS;
    this.processIntegrationResponse(res, dataField);
    this.field.formControl.setErrors(null);
    this.field.formControl.updateValueAndValidity();
    this.loading = false;
    (this as any).cd.detectChanges();
    this.emitInitialOutcomeOnce('success');
  }

  /**
   * Emit a single lookup outcome for this field's INITIAL load only. Infinite-
   * scroll pages and search keystrokes reuse the same handlers but must not emit;
   * gated on (not searching) + (not yet emitted since the last fetchInitialData).
   */
  private emitInitialOutcomeOnce(
    outcome: 'success' | 'failure',
    error?: any
  ): void {
    if (this.isSearchActive || this.hasEmittedInitialOutcome) {
      return;
    }
    this.hasEmittedInitialOutcome = true;
    this.emitFetchOutcome(outcome, error);
  }

  // Reusable request preparation method
  private prepareRequestState(): void {
    this.loading = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.field.formControl.setErrors(null);
    this.field.formControl.updateValueAndValidity();
    this.clearGlobalErrorForField();
  }

  private clearGlobalErrorForField(): void {
    try {
      (this as any).formStateService.clearGlobalError(this.field);
    } catch (error) {
      console.warn('Could not clear global error:', error);
    }
  }

  private getHttpClient(): HttpClient {
    // Access http through the base class
    return (this as any).http;
  }

  private getEnvironment(): IEnvironment {
    // Access environment through the base class
    return (this as any).environment;
  }

  private addToSubscriptions(subscription: Subscription) {
    // Add to base class subscriptions
    (this as any).subscriptions.add(subscription);
  }

  private processIntegrationResponse(
    res: Record<string, unknown>,
    dataField: string
  ) {
    try {
      const data = res['data'] as Record<string, unknown>;
      const responseData = data['response'];

      let responseObject: Record<string, unknown> = {};
      if (typeof responseData === 'string') {
        responseObject = JSON.parse(responseData);
      } else {
        responseObject = responseData as Record<string, unknown>;
      }
      let newItems: Record<string, unknown>[] = [];
      if (dataField.length === 0) {
        newItems = Array.isArray(responseObject)
          ? responseObject
          : [responseObject];
      } else {
        newItems = this.getValueFromNestedObjectLocal(
          responseObject,
          dataField
        );
      }

      if (this.currentPage === 0) {
        this.items = this.translateDatasetLabels(responseObject, dataField);
        // Cache all items for local search when not actively searching or on first load
        if (!this.isSearchActive) {
          this.allItems = [...this.items];
        }
      } else {
        const translatedNewItems = this.translateDatasetLabels(
          responseObject,
          dataField
        );
        this.items = [...this.items, ...translatedNewItems];

        // Update cache with new items when not actively searching
        if (!this.isSearchActive) {
          this.allItems = [...this.allItems, ...translatedNewItems];
        }

        // Prevent memory issues by limiting total items in dropdown to 200
        if (this.items.length > 200) {
          this.items = this.items.slice(-200);
        }
        if (this.allItems.length > 200) {
          this.allItems = this.allItems.slice(-200);
        }
      }

      // Update pagination info from response
      this.updatePaginationInfo(responseObject);
    } catch (error) {
      console.error('Error processing integration response:', error);
      this.items = [];
      this.deleteFetchedOptionsDataKeyInFormState();
    }
  }

  // Update pagination information from response
  private updatePaginationInfo(jsonData: Record<string, unknown>) {
    const { dataset } = this.field.props;
    const totalPagesKey = dataset?.['totalPagesKey'] || 'totalPages';
    const currentPageKey = dataset?.['currentPageKey'] || 'currentPage';

    try {
      const totalPages = this.getValueFromPath(jsonData, totalPagesKey);
      const currentPage = this.getValueFromPath(jsonData, currentPageKey);

      if (typeof totalPages === 'number') {
        this.totalPages = totalPages;
      }
      if (typeof currentPage === 'number') {
        this.currentPage = currentPage;
      }
    } catch (error) {
      console.warn('Pagination info not found in response:', error);
    }
  }

  // Get value from nested path
  private getValueFromPath(obj: Record<string, unknown>, path: string): any {
    if (!path) return undefined;

    const keys = path.split('.');
    let value: any = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return undefined;
      }
    }

    return value;
  }

  // Integration POST method handler
  private getDataByIntegrationPostMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {}
  ) {
    const requestParams = {
      endpointCode: this.field.props['endpointCode'],
      callerId: uuidv4(),
      payload: params,
      requester: uuidv4(),
    };

    const subscription = this.getHttpClient()
      .post<Record<string, unknown>>(url, requestParams, { headers })
      .subscribe({
        next: (res: Record<string, unknown>) => {
          this.handleIntegrationSuccess(res, dataField);
        },
        error: error => {
          this.handleIntegrationError(error);
        },
      });

    this.addToSubscriptions(subscription);
  }

  private getDataByIntegrationGetMethod(
    url: string,
    dataField: string,
    params: Record<string, unknown> = {},
    headers: Record<string, string> = {}
  ) {
    const requestParams = {
      endpointCode: this.field.props['endpointCode'],
      callerId: uuidv4(),
      payload: params,
      requester: uuidv4(),
    };

    const queryParams = Object.keys(requestParams)
      .map(key => {
        const value = requestParams[key as keyof typeof requestParams];
        if (typeof value === 'object') {
          return `${key}=${encodeURIComponent(JSON.stringify(value))}`;
        }
        return `${key}=${encodeURIComponent(value as string)}`;
      })
      .join('&');

    const subscription = this.getHttpClient()
      .get<Record<string, unknown>>(`${url}?${queryParams}`, { headers })
      .subscribe({
        next: (res: Record<string, unknown>) => {
          this.handleIntegrationSuccess(res, dataField);
        },
        error: error => {
          this.handleIntegrationError(error);
        },
      });

    this.addToSubscriptions(subscription);
  }
}
